/**
 * SolutionsHintsSection — Section 3 of QuestionEditForm.
 *
 * Renders:
 *   - Correct answer (hidden for MCQ/MCQ_MULTI/STRUCTURED, paired with
 *     error margin for NUMERICAL)
 *   - Solution image (admin only — needs onUploadImage)
 *   - Solutions by Style (variable rubric entries — handler comes from parent)
 *   - Hints (multi-line)
 *   - Max hints override
 *   - Keywords
 *   - Question Source (read-only, admin only)
 *   - Enrichment Notes (admin only)
 *
 * State is owned by the parent (`QuestionEditForm`); this is presentational.
 * Extracted from `QuestionEditForm.tsx` during the 2026-05-17 split.
 */
import { useState } from 'react';
import type { Solution } from '../../questions-page';
import { LatexEditor } from '../../latex';
import { InfoTip } from '../../components/InfoTip';
import { ImageUploadField } from '../ImageUploadField';
import { SOLUTION_STYLE_OPTIONS } from '../../question-set-types';
import type { BookOption, ConceptOption, QuestionFormData, UpdateField } from '../formTypes';
import { ConceptTagPicker } from './ConceptTagPicker';
import { QuestionOriginFields } from './QuestionOriginFields';

export interface SolutionsHintsSectionProps {
    form: QuestionFormData;
    updateField: UpdateField;
    isAdmin?: boolean;
    onUploadImage?: (base64: string, mimeType: string) => Promise<{ url: string }>;
    /** Solutions handlers — state owned by parent. */
    addSolution: () => void;
    removeSolution: (idx: number) => void;
    handleSolutionChange: (idx: number, field: keyof Solution, value: string | number | undefined) => void;
    /** Concept registry for this subject (Phase 2). When provided (admin),
     *  renders a per-solution method-concept picker. */
    conceptOptions?: ConceptOption[];
    handleSolutionConceptsChange?: (idx: number, ids: string[]) => void;
    /** Book registry for the textbook-source picker (admin). */
    bookOptions?: BookOption[];
}

export function SolutionsHintsSection({
    form,
    updateField,
    isAdmin,
    onUploadImage,
    addSolution,
    removeSolution,
    handleSolutionChange,
    conceptOptions,
    handleSolutionConceptsChange,
    bookOptions,
}: SolutionsHintsSectionProps) {
    // Per-solution: the method-concepts picker is collapsed by default (just a small
    // toggle) to keep the editor light; it's force-shown for any solution that
    // already has concepts. Tracks which empty solutions the user opened.
    const [openConcepts, setOpenConcepts] = useState<Set<number>>(new Set());
    return (
        <div className="form-section-group">
            <div className="form-section-group-header">Solution &amp; Hints</div>
            <div className="form-section-group-body">
                {/* Correct Answer — hidden for MCQ/MCQ_MULTI (set via option buttons),
                    NUMERICAL (paired with errorMargin below), and STRUCTURED
                    (answers belong on each part, or in the shared canvas). */}
                {form.questionType !== 'MCQ'
                    && form.questionType !== 'MCQ_MULTI'
                    && form.questionType !== 'NUMERICAL'
                    && form.questionType !== 'STRUCTURED' && (
                    <div className="form-section">
                        <label>Correct Answer</label>
                        <LatexEditor
                            value={form.correctAnswer}
                            onChange={v => updateField('correctAnswer', v)}
                            rows={form.questionType === 'FREEFORM' ? 5 : 1}
                            singleLine={form.questionType !== 'FREEFORM'}
                            expandable={form.questionType === 'FREEFORM'}
                        />
                    </div>
                )}

                {/* Correct Answer + Error Margin side-by-side for NUMERICAL */}
                {form.questionType === 'NUMERICAL' && (
                    <div className="form-row-2">
                        <div className="form-section">
                            <label>Correct Answer</label>
                            <LatexEditor
                                singleLine
                                value={form.correctAnswer}
                                onChange={v => updateField('correctAnswer', v)}
                            />
                        </div>
                        <div className="form-section">
                            <label>Error Margin (%)</label>
                            <input
                                type="number"
                                step="0.1"
                                min="0"
                                value={form.errorMargin ?? ''}
                                onChange={e => updateField('errorMargin', e.target.value === '' ? undefined : parseFloat(e.target.value))}
                                placeholder="0 = exact, 1 = 1%"
                            />
                        </div>
                    </div>
                )}

                {/* Solution Image (admin only) */}
                {onUploadImage && (
                    <ImageUploadField
                        label="Solution Image URL"
                        value={form.solutionImageUrl}
                        onChange={url => updateField('solutionImageUrl', url)}
                        maxPreviewHeight={160}
                        onUploadImage={onUploadImage}
                        sourceSlot={{ kind: 'solutionImage' }}
                    />
                )}

                {/* Solutions by Style */}
                <div className="form-section">
                    <div className="section-header">
                        <label>Solutions by Style</label>
                        <button type="button" className="btn-add-inline" onClick={addSolution}>+ Add</button>
                    </div>
                    {(form.solutions || []).map((solution, idx) => (
                        <div key={idx} className="solution-editor">
                            <div className="solution-editor-header">
                                <select
                                    value={solution.style || solution.proficiencyLevel || 'formal'}
                                    onChange={e => handleSolutionChange(idx, 'style', e.target.value)}
                                >
                                    {SOLUTION_STYLE_OPTIONS.map(style => (
                                        <option key={style} value={style}>{style.charAt(0).toUpperCase() + style.slice(1)}</option>
                                    ))}
                                </select>
                                <input
                                    type="number"
                                    min="1"
                                    placeholder="Time (sec)"
                                    value={solution.expectedTimeSeconds || ''}
                                    onChange={e => handleSolutionChange(idx, 'expectedTimeSeconds', e.target.value ? parseInt(e.target.value, 10) : undefined)}
                                    className="time-input"
                                />
                                <button className="btn-remove-inline" onClick={() => removeSolution(idx)}>x</button>
                            </div>
                            <LatexEditor
                                value={solution.steps}
                                onChange={v => handleSolutionChange(idx, 'steps', v)}
                                placeholder="Enter each step on a new line..."
                                rows={4}
                                expandable
                            />
                            {/* Method concepts for this solution (Phase 2; admin only).
                                Collapsed to a small toggle unless already set or opened. */}
                            {conceptOptions && handleSolutionConceptsChange && (
                                ((solution.methodConcepts?.length ?? 0) > 0 || openConcepts.has(idx)) ? (
                                    <div style={{ marginTop: 8 }}>
                                        <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Method concepts</label>
                                        <ConceptTagPicker
                                            value={solution.methodConcepts || []}
                                            onChange={ids => handleSolutionConceptsChange(idx, ids)}
                                            concepts={conceptOptions}
                                            placeholder="Concepts this method relies on…"
                                        />
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => setOpenConcepts(prev => new Set(prev).add(idx))}
                                        style={{
                                            marginTop: 8, background: 'none', border: 'none', padding: 0,
                                            cursor: 'pointer', fontSize: 12, color: 'var(--color-primary, #4f46e5)',
                                        }}
                                    >
                                        + Method concepts
                                    </button>
                                )
                            )}
                        </div>
                    ))}
                </div>

                {/* Hints */}
                <div className="form-section">
                    <label>Hints (one per line)</label>
                    <LatexEditor
                        value={(form.hints || []).join('\n')}
                        onChange={v => updateField('hints', v.split('\n'))}
                        placeholder="Enter each hint on a new line..."
                        rows={3}
                        expandable
                    />
                </div>

                {/* Max hints override (pre-written + runtime-generated) */}
                <div className="form-section">
                    <label>
                        Max Hints for Student (override)
                        <InfoTip text="Caps both the pre-written hints above and hints generated at runtime by the LLM." />
                    </label>
                    <input
                        type="number"
                        min={1}
                        max={10}
                        value={form.maxHintsForStudent ?? ''}
                        onChange={e => {
                            const v = e.target.value;
                            updateField('maxHintsForStudent', v === '' ? undefined : Number(v));
                        }}
                        placeholder="Default from global app config"
                        style={{ width: '220px' }}
                    />
                </div>

                {/* Keywords */}
                <div className="form-section">
                    <label>Keywords</label>
                    <input
                        type="text"
                        value={(form.keywords || []).join(', ')}
                        onChange={e => {
                            const val = e.target.value;
                            updateField('keywords', val ? val.split(',').map(k => k.trim()).filter(Boolean) : undefined);
                        }}
                        placeholder="Comma-separated keywords, e.g. projectile, kinematics, parabola"
                        className="form-input"
                    />
                </div>

                {/* Origin — source type + textbook/online detail (admin only) */}
                {isAdmin && (
                    <QuestionOriginFields form={form} updateField={updateField} bookOptions={bookOptions} />
                )}

                {/* Enrichment Notes (admin only) */}
                {isAdmin && (
                    <div className="form-section">
                        <label>Enrichment Notes</label>
                        <LatexEditor
                            value={form.enrichmentNotes || ''}
                            onChange={v => updateField('enrichmentNotes', v || undefined)}
                            placeholder={(form.enrichStatus ?? 0) === 0 ? 'Set enrich status to enable' : 'Notes for enrichment pipeline...'}
                            rows={3}
                            expandable
                            disabled={(form.enrichStatus ?? 0) === 0}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
